import { createFeatureSelector, createSelector } from '@ngrx/store'

import * as fromProducts from './products'
import * as fromFacets from './facets'
import * as fromBase from '../../base/reducers'
import * as fromUser from '../../auth/reducers'
import * as fromSKUs from './skus.reducer'
import * as fromStock from './stock.reducer'
import * as fromRoot from '../../reducers'
import * as fromSearch from './search.reducer'
import * as fromOrderHistory from '../../orderhistory/reducers'

import { CombinedExpandState } from '../../facets/models/combined-expand-state'
import { CombinedFilterState } from '../models/combined-filter-state'
import { FacetState } from '../../facets/models/facet-state'
import { ProductState } from '../models/product-state'

// TODO: Abstract into config
const productsPerExpand = 15

export interface CatalogState {
    products: fromProducts.State,
    facets: fromFacets.State
    skus: fromSKUs.State
    stock: fromStock.State,
    search: fromSearch.State
}

export interface State extends fromRoot.State {
    catalog: CatalogState
}

export const reducers = {
    products: fromProducts.reducer,
    facets: fromFacets.reducer,
    skus: fromSKUs.reducer,
    stock: fromStock.reducer,
    search: fromSearch.reducer
}

// Feature Selector
export const getCatalogState = createFeatureSelector<CatalogState>('catalog')

// State Selectors

export const getProductReducerState = createSelector(
    getCatalogState,
    (state: CatalogState) => state.products
)
export const getFacetReducerState = createSelector(
    getCatalogState,
    (state: CatalogState) => state.facets
)
export const getSKUReducerState = createSelector(
    getCatalogState,
    (state: CatalogState) => state.skus
)
export const getStockReducerState = createSelector(
    getCatalogState,
    (state: CatalogState) => state.stock
)
export const getSearchReducerState = createSelector(
  getCatalogState,
  (state: CatalogState) => state.search
)

// Product Selectors

export const getAll = createSelector(
    getProductReducerState,
    fromProducts.getAll
)
export const getSelectedProduct = createSelector(
    getProductReducerState,
    fromProducts.getSelected
)
export const getSelectedProductOrderHistory = createSelector(
  getProductReducerState,
  state => state && state.productOrderHistory && state.productOrderHistory.length ? state.productOrderHistory.filter(order => order.type === 'history') : []
)
export const getSelectedProductBackorders = createSelector(
  getProductReducerState,
  state => state && state.productOrderHistory && state.productOrderHistory.length ? state.productOrderHistory.filter(order => order.type === 'backorder') : []
)
export const getNextProductRequestId = createSelector(
    getProductReducerState,
    fromProducts.findNextRequestId
)
export const getStateProductCount = createSelector(
    getProductReducerState,
    fromProducts.getProductCount
)
export const getTotalProductCount = createSelector(
    getProductReducerState,
    fromProducts.total
)
export const canExpand = createSelector(
    getProductReducerState,
    state => state.totalPages > state.currentPage
)
export const getOnlyInStock = createSelector(
    getProductReducerState,
    fromProducts.onlyInStock
)
export const getProductsLoading = createSelector(
    getProductReducerState,
    fromProducts.isLoading
)
export const getProductsLoaded = createSelector(
  getProductReducerState,
  fromProducts.isLoaded
)
export const getProductsExpanding = createSelector(
  getProductReducerState,
  fromProducts.isExpanding
)
export const getProductIds = createSelector(
    getProductReducerState,
    state => state && state.ids
)

export const getNextPageNumber = createSelector(
    getProductReducerState,
    fromProducts.getNextPageNumber
)

export const getFavorites = createSelector(
  getProductReducerState,
  fromProducts.getFavorites
)

export const isGlobalProductsLoading = createSelector(
  getProductsLoading || getProductsExpanding
)

export const getProductFilters = createSelector(
  getProductReducerState,
  fromProducts.getProductFilters
)

export const getProductSortOrder = createSelector(
  getProductReducerState,
  fromProducts.getProductSortOrder,
)

// Search Selectors
export const getSearchTerm = createSelector(
  getSearchReducerState,
  fromSearch.getSearchTerm
)

// Facet Selectors
export const isFacetsReady = createSelector(
    getFacetReducerState,
    fromFacets.isInitializing,
    (state, initializing) => !initializing
)
export const getAppliedFacets = createSelector(
    getFacetReducerState,
    fromFacets.getApplied
)
export const hasAppliedFacets = createSelector(
    getFacetReducerState,
    fromFacets.hasApplied
)
export const hasGroups = createSelector(
    getFacetReducerState,
    fromFacets.hasGroups
)
export const hasFacetBaseline = createSelector(
    getFacetReducerState,
    fromFacets.hasBaseline
)
export const getUXFacetGroups = createSelector(
    getFacetReducerState,
    fromFacets.getFacetsUX
)
export const getUXFacetChips = createSelector(
    getFacetReducerState,
    fromFacets.getFacetChips
)
export const getOpenGroups = createSelector(
    getFacetReducerState,
    fromFacets.getOpenGroups
)
export const getCombinedFacets = createSelector(
  getFacetReducerState,
  fromFacets.combinedFacets
)
export const getProductFilterables = createSelector(
    getAppliedFacets,
    getProductFilters,
    fromBase.getRoutePageId,
    getOnlyInStock,
    fromUser.getCustomerId,
    getSearchTerm,
    (facets, filterItems, pageId, onlyInStock, customerId, searchTerm) => ({ facets, filterItems, pageId, onlyInStock, customerId, searchTerm })
)
export const isFacetGroupsTtlExpired = createSelector(
  getFacetReducerState,
  fromFacets.isFacetGroupsTtlExpired
)
export const isFacetsTtlExpired = createSelector(
  getFacetReducerState,
  fromFacets.isFacetsTtlExpired
)

// SKUs Selectors

export const isSKUsLoading = createSelector(
    getSKUReducerState,
    fromSKUs.isLoading
)
export const getSKUs = createSelector(
    getSKUReducerState,
    fromSKUs.getSKUs
)
export const getSKUError = createSelector(
    getSKUReducerState,
    fromSKUs.getError
)

// Stock Selectors

export const isStockLoading = createSelector(
    getStockReducerState,
    fromStock.isLoading
)
export const getStock = createSelector(
    getStockReducerState,
    fromStock.getStock
)
export const getStockError = createSelector(
    getStockReducerState,
    fromStock.getError
)

// Combined Selectors
export const getCombinedExpandState = createSelector(
    fromUser.getCustomerId,
    fromBase.getRoutePageId,
    getNextProductRequestId,
    getAppliedFacets,
    getOnlyInStock,
    getNextPageNumber,
    getSearchTerm,
    getProductFilters,
    (customerId, pageId, nextRequestId, facets, onlyInStock, nextPage, searchTerm, filterItems) => new CombinedExpandState(
        customerId,
        nextRequestId,
        facets,
        pageId,
        onlyInStock,
        nextPage,
        searchTerm,
        filterItems
    )
)
export const getCombinedFilterState = createSelector(
    fromBase.getRoutePageId,
    getAppliedFacets,
    getOnlyInStock,
    (pageId, facets, onlyInStock) => ({
        pageId,
        facets,
        onlyInStock
    } as CombinedFilterState)
)
export const isInitialized = createSelector(
    hasGroups,
    hasFacetBaseline,
    (hasGroups, hasBaseline) => hasGroups && hasBaseline
)
export const isProductsLoading = createSelector(
    isFacetsReady,
    getProductsLoading,
    (facetsReady, productsLoading) => !facetsReady || productsLoading
)

// Combined State Selectors
export const getFacetState = createSelector(
    isFacetsReady,
    getUXFacetGroups,
    getUXFacetChips,
    getOpenGroups,
    getAppliedFacets,
    (ready, groups, chips, open, applied) =>
        (<FacetState>{loading: !ready, groups, chips, open, applied})
)
export const getFacetsStateForProducts = createSelector(
  getFacetReducerState,
  fromFacets.getBaseline
)
export const getProductState = createSelector(
    isProductsLoading,
    getOnlyInStock,
    getAll,
    getTotalProductCount,
    getProductsExpanding,
    (loading, onlyStock, products, total, expanding) => (<ProductState>{loading, onlyStock, products, total, expanding})
)

export const isDetailsLoading = createSelector(
    getProductsLoading,
    isSKUsLoading,
    isStockLoading,
    (productsLoading, skusLoading, stockLoading) =>
        productsLoading || skusLoading || stockLoading
)

